export const REPLAY_DIVERGENCE_VERSION = 1;

const clone = (value) => value === undefined
  ? undefined
  : JSON.parse(JSON.stringify(value));

function deepFreeze(value) {
  if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value;
  for (const child of Object.values(value)) deepFreeze(child);
  return Object.freeze(value);
}

function assertSnapshot(snapshot, name) {
  if (!snapshot || typeof snapshot !== 'object' || Array.isArray(snapshot)) {
    throw new TypeError(`${name} must be a simulation snapshot object`);
  }
}

function assertSnapshotList(snapshots, name) {
  if (!Array.isArray(snapshots)) throw new TypeError(`${name} must be an array`);
  snapshots.forEach((snapshot, index) => assertSnapshot(snapshot, `${name}[${index}]`));
}

function diagnosticValue(value) {
  return value === undefined ? { type: 'undefined' } : clone(value);
}

function joinPath(path, key) {
  if (typeof key === 'number') return `${path}[${key}]`;
  return path ? `${path}.${key}` : key;
}

function diffValues(expected, actual, path = '') {
  if (Object.is(expected, actual)) return [];

  if (Array.isArray(expected) && Array.isArray(actual)) {
    const differences = [];
    const length = Math.max(expected.length, actual.length);
    for (let index = 0; index < length; index += 1) {
      differences.push(...diffValues(expected[index], actual[index], joinPath(path, index)));
    }
    return differences;
  }

  const expectedObject = expected && typeof expected === 'object' && !Array.isArray(expected);
  const actualObject = actual && typeof actual === 'object' && !Array.isArray(actual);
  if (expectedObject && actualObject) {
    const differences = [];
    const keys = [...new Set([...Object.keys(expected), ...Object.keys(actual)])].sort();
    for (const key of keys) {
      differences.push(...diffValues(expected[key], actual[key], joinPath(path, key)));
    }
    return differences;
  }

  return [{
    path: path || '$',
    expected: diagnosticValue(expected),
    actual: diagnosticValue(actual),
  }];
}

function compareSection(expected, actual, path) {
  const differences = diffValues(expected, actual, path);
  return deepFreeze({
    match: differences.length === 0,
    differences,
  });
}

function createMissingSnapshotComparison(expected, actual, fallbackTick) {
  const missing = expected ? 'actual' : 'expected';
  const tick = expected?.tick ?? actual?.tick ?? fallbackTick;
  return deepFreeze({
    version: REPLAY_DIVERGENCE_VERSION,
    ok: false,
    divergent: true,
    tick,
    missing,
    expectedChecksum: expected?.checksum ?? null,
    actualChecksum: actual?.checksum ?? null,
    checksumMatch: false,
    sections: {
      snapshot: compareSection(expected, actual, 'snapshot'),
    },
    differenceSections: ['snapshot'],
    differenceCount: 1,
  });
}

export function compareReplaySnapshots(expected, actual) {
  assertSnapshot(expected, 'expected');
  assertSnapshot(actual, 'actual');

  const sections = {
    identity: compareSection(
      { version: expected.version, tick: expected.tick },
      { version: actual.version, tick: actual.tick },
      'identity',
    ),
    timing: compareSection(
      { stepSeconds: expected.stepSeconds, simulationTime: expected.simulationTime },
      { stepSeconds: actual.stepSeconds, simulationTime: actual.simulationTime },
      'timing',
    ),
    input: compareSection(expected.input, actual.input, 'input'),
    replay: compareSection(expected.replay, actual.replay, 'replay'),
    randomState: compareSection(expected.randomState, actual.randomState, 'randomState'),
    worldStateRef: compareSection(expected.worldStateRef, actual.worldStateRef, 'worldStateRef'),
    metadata: compareSection(expected.metadata, actual.metadata, 'metadata'),
  };
  const differenceSections = Object.entries(sections)
    .filter(([, result]) => !result.match)
    .map(([name]) => name);
  const differenceCount = Object.values(sections)
    .reduce((total, result) => total + result.differences.length, 0);
  const expectedChecksum = typeof expected.checksum === 'string' ? expected.checksum : null;
  const actualChecksum = typeof actual.checksum === 'string' ? actual.checksum : null;
  const checksumMatch = expectedChecksum !== null && expectedChecksum === actualChecksum;
  const ok = checksumMatch && differenceSections.length === 0;

  return deepFreeze({
    version: REPLAY_DIVERGENCE_VERSION,
    ok,
    divergent: !ok,
    tick: actual.tick ?? expected.tick ?? null,
    missing: null,
    expectedChecksum,
    actualChecksum,
    checksumMatch,
    sections,
    differenceSections,
    differenceCount,
  });
}

export class ReplayDivergenceDetector {
  constructor() {
    this.reset();
  }

  start(expectedSnapshots, { stopAtFirst = true } = {}) {
    assertSnapshotList(expectedSnapshots, 'expectedSnapshots');
    if (expectedSnapshots.length === 0) {
      throw new RangeError('expectedSnapshots must contain at least one snapshot');
    }

    this.expectedSnapshots = clone(expectedSnapshots);
    this.stopAtFirst = Boolean(stopAtFirst);
    this.active = true;
    this.completed = false;
    this.stoppedOnDivergence = false;
    this.cursor = 0;
    this.firstDivergence = null;
    return this.getStatus();
  }

  observe(actualSnapshot) {
    if (!this.active) return null;
    assertSnapshot(actualSnapshot, 'actualSnapshot');

    const expectedSnapshot = this.expectedSnapshots[this.cursor] || null;
    const comparison = expectedSnapshot
      ? compareReplaySnapshots(expectedSnapshot, actualSnapshot)
      : createMissingSnapshotComparison(null, actualSnapshot, this.cursor);

    this.cursor += 1;
    if (!comparison.ok && !this.firstDivergence) this.firstDivergence = comparison;

    if (!comparison.ok && this.stopAtFirst) {
      this.active = false;
      this.completed = true;
      this.stoppedOnDivergence = true;
    }

    return comparison;
  }

  finish() {
    if (!this.active) return this.getStatus();

    if (this.cursor < this.expectedSnapshots.length && !this.firstDivergence) {
      this.firstDivergence = createMissingSnapshotComparison(
        this.expectedSnapshots[this.cursor],
        null,
        this.cursor,
      );
    }

    this.active = false;
    this.completed = true;
    return this.getStatus();
  }

  reset() {
    this.expectedSnapshots = [];
    this.stopAtFirst = true;
    this.active = false;
    this.completed = false;
    this.stoppedOnDivergence = false;
    this.cursor = 0;
    this.firstDivergence = null;
    return this.getStatus();
  }

  getStatus() {
    return deepFreeze({
      version: REPLAY_DIVERGENCE_VERSION,
      active: this.active,
      completed: this.completed,
      ok: this.completed ? this.firstDivergence === null : this.firstDivergence ? false : null,
      stopAtFirst: this.stopAtFirst,
      stoppedOnDivergence: this.stoppedOnDivergence,
      expectedCount: this.expectedSnapshots.length,
      comparedTicks: this.cursor,
      nextExpectedTick: this.expectedSnapshots[this.cursor]?.tick ?? null,
      firstDivergence: clone(this.firstDivergence),
    });
  }

  exportDiagnostic(metadata = {}) {
    const diagnostic = deepFreeze({
      version: REPLAY_DIVERGENCE_VERSION,
      kind: 'serpent-replay-divergence',
      generatedAt: new Date().toISOString(),
      metadata: clone(metadata) || {},
      status: this.getStatus(),
    });
    return JSON.stringify(diagnostic, null, 2);
  }
}

export function compareReplayHistories(expectedSnapshots, actualSnapshots, options = {}) {
  assertSnapshotList(expectedSnapshots, 'expectedSnapshots');
  assertSnapshotList(actualSnapshots, 'actualSnapshots');
  if (expectedSnapshots.length === 0) {
    throw new RangeError('expectedSnapshots must contain at least one snapshot');
  }

  const detector = new ReplayDivergenceDetector();
  detector.start(expectedSnapshots, options);
  for (const snapshot of actualSnapshots) {
    detector.observe(snapshot);
    if (!detector.active) break;
  }
  if (detector.active) detector.finish();

  return deepFreeze({
    ...detector.getStatus(),
    actualCount: actualSnapshots.length,
    lengthMatch: expectedSnapshots.length === actualSnapshots.length,
  });
}
